import { PromptStepForm } from "@/components/PromptStepForm";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { PromptHeader } from "@/components/strategy-deck/PromptHeader";
import { RequiredInformation } from "@/components/strategy-deck/RequiredInformation";
import { HowItWorks } from "@/components/strategy-deck/HowItWorks";
import { ExpectedOutputs } from "@/components/strategy-deck/ExpectedOutputs";

const StrategyDeckPrompt = () => {
  const promptData = {
    title: "Product Decision Document Generator",
    description: "Create structured, well-reasoned product decision documents that capture key context, recommendations, and next steps. Our AI-powered assistant will help guide the decision-making process and document the final outcome.",
    promptTemplate: `You are a product leader who's an expert at analyzing complex product decisions and providing well-reasoned recommendations. Your task is to guide the decision-making process through thoughtful discussion and document the final decision.

Here is the format you should use for the final decision document:

<decision_doc_format>
  <context>
    [Brief overview of the decision needed and key factors considered]
  </context>

  <recommendation>
    [Recommended option with 3-5 bullets highlighting the pros. Also, include up to 3 bullets about the risks and mitigations. Each bullet should have 2-3 sentences]
  </recommendation>

  <alternatives>
    [List up to 3 other options. For each option, have a bullet for the pros and another for the cons. Each bullet should have 2-3 sentences.]
  </alternatives>

  <next_steps>
    [Suggest specific actions to implement the recommendation]
  </next_steps>
</decision_doc_format>

Please follow these instructions carefully:

1. Ask for information about the following all at once:
   - The specific decision needed
   - Customer pain points and goals
   - Goals and success metrics
   - Options being considered
   - Timeline and constraints

2. Next, have a collaborative discussion with me to:
   - Explore each option's pros and cons
   - Test assumptions with real scenarios
   - Surface hidden risks and dependencies
   - Consider hybrid approaches
   - Challenge thinking with "what if" scenarios

3. Once it looks like we're reaching alignment, propose creating a decision doc about the discussion using the <decision_doc_format> provided earlier.`,
    imageSrc: "/lovable-uploads/12e8cf98-655e-45cd-ae0f-be886212daff.png"
  };

  return (
    <main className="min-h-screen bg-gradient-radial from-accent to-white py-12">
      <div className="max-w-4xl mx-auto px-4">
        <BackButton />
        <PromptHeader 
          imageSrc={promptData.imageSrc}
          title={promptData.title}
          description={promptData.description}
        />
        
        <div className="bg-white rounded-lg p-6 mb-8">
          <div className="space-y-8">
            <RequiredInformation />
            <HowItWorks />
            <ExpectedOutputs />

            <div>
              <PromptStepForm promptTemplate={promptData.promptTemplate} />
            </div>

            <Collapsible>
              <CollapsibleTrigger className="flex items-center justify-between w-full text-lg font-medium mb-4">
                Example Prompt
                <ChevronDown className="h-5 w-5 transition-transform duration-200" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <pre className="whitespace-pre-wrap text-sm bg-accent p-4 rounded-lg">
                  {promptData.promptTemplate}
                </pre>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>
    </main>
  );
};

export default StrategyDeckPrompt;
